import React, { useRef } from 'react';
import { FiEdit, FiPlus, FiTrash2 } from 'react-icons/fi';
import ReactPlayer from 'react-player';
import styled from 'styled-components';
import { HLS_MIMETYPE } from '../../../util/mux-utilities';
import { HorizontalContent } from '../HorizontalContent';
import { RhButton, RhButtonMode } from '../RhButton';
import { RhCard } from '../RhCard';
import { H2 } from '../Styles';

type Props = {
  /** the current form state */
  form: { introVideo: File | undefined };
  /** called when the user edits the fields */
  userUpdatedForm: (formState: { introVideo: File | undefined }) => void;
  /** invoked when user clicks delete button */
  userClickedDelete: () => void;
};

export const IntroductionVideoCard: React.FC<Props> = props => {
  const introVideoRef = useRef<HTMLInputElement>(null);

  return (
    <RhCard mode="tiled">
      <HorizontalContent
        left={<H2>Introduction Video</H2>}
        right={[
          <input
            key="bsp-add-intro-vid"
            type="file"
            hidden={true}
            accept="video/*"
            multiple={false}
            ref={introVideoRef}
            onChange={e => {
              if (e.target.files?.[0]) {
                props.userUpdatedForm({
                  ...props.form,
                  introVideo: e.target.files?.[0],
                });
              }
            }}
          />,
          <RhButton
            key="bsp-add-intro-vid-btn"
            mode={RhButtonMode.subtle}
            prefixIcon={props.form.introVideo ? FiEdit : FiPlus}
            iconSize={18}
            onClick={() => {
              introVideoRef.current?.click();
            }}
          />,
          props.form.introVideo && (
            <RhButton
              key="bsp-remove-intro-vid-btn"
              mode={RhButtonMode.subtle}
              prefixIcon={FiTrash2}
              iconSize={18}
              onClick={props.userClickedDelete}
            />
          ),
        ]}
      />
      {!!props.form.introVideo && (
        <PlayerDiv>
          <ReactPlayer
            url={
              props.form.introVideo.type === HLS_MIMETYPE
                ? // if video is a m3u8, it's generated from muxId. set filename (which is a mux hls link)
                  // see util/MuxHelpers/generateFileFromPlaybackId
                  props.form.introVideo.name
                : // if video is anything else (like an mp4), it came from the computer. set objectUrl
                  URL.createObjectURL(props.form.introVideo)
            }
            controls={true}
            width="100%"
            height="100%"
            style={styles.player}
          />
        </PlayerDiv>
      )}
    </RhCard>
  );
};

const styles: {
  [key: string]: React.CSSProperties;
} = {
  player: {
    borderRadius: 16,
    overflow: 'hidden',
    position: 'absolute',
    top: 0,
    left: 0,
  },
};

const PlayerDiv = styled.div`
  width: 100%;
  margin-top: 12px;
  position: relative;
  // fixed aspect ratio box
  // see: https://github.com/cookpete/react-player#responsive-player
  padding-top: 56.25%;
`;
